/**
 * The ledger phase: loads the completed run from this browser's database and
 * mounts the view. The menu holds exactly what the brief allows — run again,
 * the disclosure to re-read, clear all data — and nothing else.
 */
import { useEffect, useMemo, useState } from 'react'
import { DisclosureText } from '../Disclose/index.js'
import { localRoutingByItem, useActions } from '../../actions/index.js'
import { useAppState } from '../../store/appState.js'
import { clearAllData, db } from '../../store/db.js'
import type { RunRow } from '../../store/db.js'
import { buildScoresExport, downloadJson, scoredCount, useScores } from '../../study/index.js'
import { LedgerView } from './LedgerView.js'
import type { ActionHooks } from './LedgerView.js'
import { firstCardId } from './sections.js'
import styles from './Ledger.module.scss'

/**
 * Correct opens the correction sheet (M4.1, COR-64). Until the sheet exists
 * the verb is in its place on the sections the table names, unavailable, and
 * says why rather than pretending to work.
 */
const CORRECT_UNAVAILABLE = 'Corrections are not ready yet.'

export function Ledger() {
  const runId = useAppState((s) => s.run_id)
  const runAgain = useAppState((s) => s.runAgain)
  const reset = useAppState((s) => s.reset)
  const study = useAppState((s) => s.study)
  const scores = useScores((s) => s.scores)
  const scoresFor = useScores((s) => s.cutoff)
  const loadScores = useScores((s) => s.load)
  const score = useScores((s) => s.score)
  const corrections = useActions((s) => s.corrections)
  const recomputing = useActions((s) => s.recomputing)
  const actionsHydrated = useActions((s) => s.hydrated)
  const actionsError = useActions((s) => s.error)
  const loadActions = useActions((s) => s.load)
  const runAction = useActions((s) => s.run)
  const undoAction = useActions((s) => s.undo)
  const clearActions = useActions((s) => s.clear)
  const [run, setRun] = useState<RunRow | null | undefined>(undefined)
  const [showDisclosure, setShowDisclosure] = useState(false)
  const [confirmClear, setConfirmClear] = useState(false)

  useEffect(() => {
    if (!runId) return
    let live = true
    void db()
      .runs.get(runId)
      .then((row) => {
        if (live) setRun(row ?? null)
      })
      .catch(() => {
        if (live) setRun(null)
      })
    return () => {
      live = false
    }
  }, [runId])

  useEffect(() => {
    if (study && run && scoresFor !== run.cutoff) void loadScores(run.cutoff)
  }, [study, run, scoresFor, loadScores])

  // The correction log is where the screen learns what you have already done.
  useEffect(() => {
    if (!actionsHydrated) void loadActions()
  }, [actionsHydrated, loadActions])

  const ledger = run?.ledger
  const routing = useMemo(
    () =>
      ledger
        ? localRoutingByItem(ledger.items, corrections, ledger.identity)
        : new Map<never, never>(),
    [ledger, corrections],
  )

  if (!runId || run === null)
    return (
      <section className={styles.panel}>
        <p>The reconstruction is no longer in this browser.</p>
        <button type="button" className={styles.quiet} onClick={runAgain}>
          Run again
        </button>
      </section>
    )

  if (run === undefined) return <p className={styles.loading}>Opening your ledger…</p>

  const skipTo = firstCardId(run.ledger, undefined, routing)
  const actions: ActionHooks = {
    routing,
    recomputing,
    error: actionsError,
    correctUnavailable: CORRECT_UNAVAILABLE,
    onAction: (item, spec, party) => void runAction({ spec, item, ledger: run.ledger, party }),
    onUndo: (item, itemRouting) => void undoAction({ item, routing: itemRouting }),
  }

  return (
    <div className={styles.screen}>
      {skipTo && (
        <a
          className={styles.skipLink}
          href={`#${skipTo}`}
          onClick={(event) => {
            // Move focus without writing the hash: the hash is where study mode lives.
            event.preventDefault()
            document.getElementById(skipTo)?.focus()
          }}
        >
          Skip to the first card
        </a>
      )}
      <nav className={styles.menu} aria-label="Ledger menu">
        <button type="button" className={styles.quiet} onClick={runAgain}>
          Run again
        </button>
        <button
          type="button"
          className={styles.quiet}
          aria-expanded={showDisclosure}
          onClick={() => setShowDisclosure((v) => !v)}
        >
          Disclosure
        </button>
        <button type="button" className={styles.quiet} onClick={() => setConfirmClear(true)}>
          Clear all data
        </button>
        {study && (
          <button
            type="button"
            className={styles.quiet}
            onClick={() =>
              downloadJson('scores.json', buildScoresExport(run.ledger, run.cutoff, scores))
            }
          >
            Export scores
          </button>
        )}
      </nav>
      {showDisclosure && (
        <section className={styles.panel} aria-label="Disclosure">
          <DisclosureText />
        </section>
      )}
      {confirmClear && (
        <section className={styles.panel} role="alertdialog" aria-labelledby="clear-title">
          <p id="clear-title">
            This removes the reconstruction and its cache from this browser. Nothing else exists
            anywhere.
          </p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.quiet}
              onClick={() => {
                void clearAllData().then(() => {
                  clearActions()
                  reset()
                })
              }}
            >
              Delete everything
            </button>
            <button type="button" className={styles.quiet} onClick={() => setConfirmClear(false)}>
              Keep it
            </button>
          </div>
        </section>
      )}
      <LedgerView
        ledger={run.ledger}
        cutoff={run.cutoff}
        windowDays={run.window_days}
        messagesRead={run.ledger.summary.messages_in_window}
        actions={actions}
        {...(study
          ? {
              scoring: {
                scores,
                scored: scoredCount(scores),
                onScore: (item_id, field, value) => void score(item_id, field, value),
              },
            }
          : {})}
      />
    </div>
  )
}
